import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap, GeoJSONSource, ExpressionSpecification } from 'maplibre-gl';
import type { PartnerMapMarker, PartnerMapViewport } from '@erp/shared';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import 'maplibre-gl/dist/maplibre-gl.css';
import mapWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url';

export function PartnerMapCanvas({
  styleUrl,
  markers,
  selected,
  onViewport,
  onSelect,
  onPoint,
  picking,
}: {
  styleUrl: string;
  markers: PartnerMapMarker[];
  selected: { latitude: number; longitude: number; zoom?: number } | null;
  onViewport: (value: PartnerMapViewport) => void;
  onSelect: (marker: PartnerMapMarker) => void;
  onPoint: (latitude: number, longitude: number) => void;
  picking: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const callbacks = useRef({ onViewport, onSelect, onPoint, picking, markers });
  callbacks.current = { onViewport, onSelect, onPoint, picking, markers };
  const camera = useRef<{ center: [number, number]; zoom: number }>({ center: [5, 48], zoom: 3 });
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let disposed = false;
    let resize: ResizeObserver | undefined;
    setError(false);
    void import('maplibre-gl')
      .then(({ Map, NavigationControl, setWorkerUrl }) => {
        if (disposed || !container.current) return;
        // Serve the standalone worker unchanged: bundling the inline worker can leak helpers outside its scope.
        setWorkerUrl(mapWorkerUrl);
        const instance = new Map({
          container: container.current,
          style: styleUrl,
          ...camera.current,
          attributionControl: { compact: false },
        });
        map.current = instance;
        instance.addControl(new NavigationControl({ showCompass: false }));
        const emit = () => {
          const center = instance.getCenter();
          camera.current = { center: [center.lng, center.lat], zoom: instance.getZoom() };
          const bounds = instance.getBounds();
          const wrap = (value: number) => ((((value + 180) % 360) + 360) % 360) - 180;
          callbacks.current.onViewport({
            west: bounds.getEast() - bounds.getWest() >= 360 ? -180 : wrap(bounds.getWest()),
            east: bounds.getEast() - bounds.getWest() >= 360 ? 180 : wrap(bounds.getEast()),
            south: Math.max(-90, bounds.getSouth()),
            north: Math.min(90, bounds.getNorth()),
            zoom: instance.getZoom(),
          });
        };
        instance.on('style.load', () => {
          if (disposed) return;
          const css = getComputedStyle(container.current ?? document.documentElement);
          const color = (token: string) => `hsl(${css.getPropertyValue(token).trim()})`;
          instance.addSource('partners', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
          const roleColor: ExpressionSpecification = [
            'case',
            ['get', 'mixed'],
            color('--primary'),
            ['get', 'supplier'],
            color('--chart-3'),
            ['get', 'prospect'],
            color('--chart-2'),
            color('--chart-1'),
          ];
          instance.addLayer({
            id: 'partners',
            type: 'circle',
            source: 'partners',
            paint: {
              'circle-radius': ['case', ['>', ['get', 'locations'], 1], 18, 10],
              'circle-color': [
                'case',
                ['>', ['get', 'locations'], 1],
                color('--background'),
                roleColor,
              ],
              'circle-stroke-width': ['case', ['get', 'selected'], 5, 2],
              'circle-stroke-color': [
                'case',
                ['>', ['get', 'locations'], 1],
                roleColor,
                color('--background'),
              ],
            },
          });
          // Use a font supplied by this style; MapLibre defaults are absent from OpenFreeMap.
          const fonts = instance
            .getStyle()
            .layers.flatMap((layer) =>
              layer.type === 'symbol' ? [layer.layout?.['text-font']] : [],
            )
            .filter(
              (font): font is string[] =>
                Array.isArray(font) &&
                font.length > 0 &&
                font.every((name) => typeof name === 'string'),
            );
          const font = fonts.find((names) => names.some((name) => /regular/i.test(name))) ??
            fonts[0] ?? ['Noto Sans Regular'];
          instance.addLayer({
            id: 'partner-counts',
            type: 'symbol',
            source: 'partners',
            layout: {
              'text-field': [
                'case',
                ['>', ['get', 'locations'], 1],
                ['to-string', ['get', 'locations']],
                '',
              ],
              'text-size': 12,
              'text-font': font,
            },
            paint: { 'text-color': color('--foreground') },
          });
          setRevision((value) => value + 1);
          emit();
        });
        instance.on('moveend', emit);
        instance.on('error', () => {
          if (!disposed) setError(true);
        });
        instance.on('click', (event) => {
          if (callbacks.current.picking) {
            callbacks.current.onPoint(event.lngLat.lat, event.lngLat.wrap().lng);
            return;
          }
          if (!instance.getLayer('partners')) return;
          const id = instance.queryRenderedFeatures(event.point, { layers: ['partners'] })[0]
            ?.properties?.['id'];
          const marker = callbacks.current.markers.find((item) => item.id === id);
          if (marker) callbacks.current.onSelect(marker);
        });
        resize = new ResizeObserver(() => instance.resize());
        resize.observe(container.current);
      })
      .catch(() => setError(true));
    return () => {
      disposed = true;
      resize?.disconnect();
      map.current?.remove();
      map.current = null;
    };
  }, [styleUrl, retry]);
  useEffect(() => {
    const source = map.current?.getSource<GeoJSONSource>('partners');
    source?.setData({
      type: 'FeatureCollection',
      features: markers.map((marker) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [marker.longitude, marker.latitude] },
        properties: {
          ...marker,
          selected:
            selected?.latitude === marker.latitude && selected?.longitude === marker.longitude,
          mixed: [marker.customer, marker.prospect, marker.supplier].filter(Boolean).length > 1,
        },
      })),
    });
  }, [markers, revision, selected]);
  useEffect(() => {
    if (selected)
      map.current?.flyTo({
        center: [selected.longitude, selected.latitude],
        zoom: selected.zoom ?? Math.max(map.current.getZoom(), 13),
        essential: false,
      });
  }, [selected, revision]);
  return (
    <div className="partner-map relative min-w-0">
      <div
        ref={container}
        role="region"
        aria-label={t('partnerMap.map')}
        className="h-[45vh] min-h-80 rounded-md border border-border md:h-[65vh]"
      />
      {error ? (
        <div
          role="status"
          className="absolute bottom-10 left-2 right-2 rounded border border-border bg-background p-3 text-sm"
        >
          {t('partnerMap.tileError')}{' '}
          <Button
            variant="outline"
            onClick={() => {
              setError(false);
              setRetry((value) => value + 1);
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
